import type { StashtCardEntity } from "@/types/stashtcard";

const ROUTING_CLASS_MAP: Record<string, string> = {
  Place: "LOCATION",
  GeoArea: "AREA",
  PersonOrgBrand: "BRAND",
  Eventish: "EVENT",
  CreativeWork: "CREATIVE",
  Product: "PRODUCT",
  Abstract: "CONCEPT",
};

const normaliseType = (routingClass?: string) => {
  if (!routingClass) return "OTHER";
  return ROUTING_CLASS_MAP[routingClass] || routingClass.toUpperCase();
};

const normaliseName = (apiEntity: any) => {
  return (
    apiEntity?.name ||
    apiEntity?.entity_name ||
    ""
  );
};

const normaliseLocation = (location: any): string | undefined => {
  if (!location) return undefined;
  if (typeof location === "string") return location;
  // Handle {name, country, state, city} object format
  if (typeof location === "object" && location.name) return location.name;
  return undefined;
};

export const mapApiEntityToCardEntity = (apiEntity: any): (StashtCardEntity & { entity_id?: string }) | null => {
  if (!apiEntity) return null;

  // Read from top-level (new API) with fallback to enrichment_data for Google Places data
  const enrichmentData = apiEntity.enrichment_data ?? {};
  const routingClass = apiEntity.routing_class || "OTHER";
  const type = normaliseType(routingClass);
  const name = normaliseName(apiEntity);
  const entityId = apiEntity.entity_id || apiEntity.id || apiEntity.entityId;

  const entity: StashtCardEntity & { entity_id?: string } = {
    id: entityId || name,
    entity_id: entityId,
    name,
    type,
    routing_class: routingClass,
    category: apiEntity.category,
    tags: enrichmentData.types,
    location: normaliseLocation(apiEntity.location) || enrichmentData.formatted_address,
    address: apiEntity.address,
    business_hours: apiEntity.business_hours,
    rating: enrichmentData.rating,
    phone: enrichmentData.phone_number,
    website: enrichmentData.website,
    google_maps_uri: enrichmentData.google_maps_uri,
    place_id: enrichmentData.place_id || apiEntity.google_place_id,
    temporal: apiEntity.temporal,
    ticketmaster_status: enrichmentData.status,
    details: apiEntity.details,
    description: enrichmentData.description,
    url: enrichmentData.url,
    enrichment_status: enrichmentData.status,
    search_query: enrichmentData.search_query,
    price: apiEntity.price,
  };

  return entity;
};

export const extractEntityIdsFromPost = (post: any): string[] => {
  const ids = new Set<string>();

  const ensureId = (value?: string | null) => {
    if (value) {
      ids.add(value);
    }
  };

  if (Array.isArray(post?.entity_ids)) {
    post.entity_ids.forEach(id => ensureId(typeof id === "string" ? id : `${id}`));
  }

  // Secondary: extracted entities grouped by type (now at top-level)
  const extracted = post?.extracted_entities;
  if (extracted) {
    Object.values(extracted).forEach(group => {
      if (Array.isArray(group)) {
        group.forEach((item: any) => {
          ensureId(item?.entity_id || item?.id || item?.entityId);
        });
      }
    });
  }

  return Array.from(ids);
};

// --- Collection entity helpers ---

export interface CollectionEntityEntry {
  entity_id: string;
  name: string;
  group: string; // "places" | "people" | "brands" | "products" | "events" | "activities"
  subtitle?: string;
}

/**
 * Build the toast label for a successful "add to collection" operation.
 */
export function buildAddSuccessLabel(opts: {
  stashCount: number;
  entityCount: number;
  collectionTitle?: string;
}): string {
  const { stashCount, entityCount, collectionTitle } = opts;
  const target = collectionTitle ? ` to ${collectionTitle}` : "";

  if (stashCount > 0 && entityCount > 0) {
    const stashWord = stashCount === 1 ? "stash" : "stashes";
    const entityWord = entityCount === 1 ? "discovery" : "discoveries";
    return `Added ${stashCount} ${stashWord} and ${entityCount} ${entityWord}${target}`;
  }
  if (entityCount > 0) {
    const entityWord = entityCount === 1 ? "discovery" : "discoveries";
    return `Added ${entityCount} ${entityWord}${target}`;
  }
  if (stashCount > 1) {
    return `Added ${stashCount} stashes${target}`;
  }
  return `Added${target}`;
}
